import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from '@xstate/react';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketLoadingPanel,
} from '~/features/shared/market-content';
import { ResponsiveFilters } from '~/features/shared/market-content/ResponsiveFilters';
import { useSearchFit } from '~/features/shared/market-content/useSearchFit';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { ListingSide } from '../b2bSideCopy';
import { useMsaasListingSearchRef } from '../marketSelectors';
import { useCompareListings } from './compareListings';
import { FIT_MAX_POLLS, FIT_POLL_INTERVAL_MS, SORT_BY_OPTIONS } from './consts';
import { countAppliedFilters } from './filterCount';
import { OFFERING_SORT_OPTIONS, offeringSearchSchema } from './offeringSearch';
import { ListingProviderCategories } from './ProviderCategoriesContext';
import {
  selectIsSearchViewing,
  selectPendingFitKey,
  selectSearchEmployerUser,
  selectSearchFilters,
  selectSearchListings,
  selectSearchLoadedAt,
  selectSearchLoading,
  selectSearchPagination,
  selectSearchSortBy,
} from './selectors';
import { ActionRetryNotice } from './sub-components/ActionButtons';
import { ActionModal } from './sub-components/ActionModal';
import { DetailPanel } from './sub-components/DetailPanel';
import { ListingsList } from './sub-components/ListingsList';
import { OfferingListingsList } from './sub-components/OfferingListingsList';
import { OfferingSearchFilters, useOfferingPrefill } from './sub-components/OfferingSearchFilters';
import { SearchAndFilter } from './sub-components/SearchAndFilter/SearchAndFilter';
import {
  searchAndFilterSchema,
  SearchAndFilterValues,
} from './sub-components/SearchAndFilter/types';
import type { SearchFilters } from './types';

const SIDEBAR_MIN = 300;
const SIDEBAR_MAX = 420;
const SIDEBAR_DEFAULT = 350;

// Re-reads the Buying results while any fit is still being scored. ON_REFRESH is ignored outside
// $_VIEW (a request or message modal, a read in flight), so the timer is only set there and is set
// again on every return to it, including after a read that failed and left the results as they were.
// The poll budget is per set of pending rows and per search: a new Check fit, search, sort or page
// starts it over, so a row stuck pending for a minute does not leave a later one unpolled.
const useOfferingFitPolling = (enabled: boolean) => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const pendingKey = useSelector(msaasListingsSearchRef, selectPendingFitKey);
  const loadedAt = useSelector(msaasListingsSearchRef, selectSearchLoadedAt);
  const viewing = useSelector(msaasListingsSearchRef, selectIsSearchViewing);
  const searched = useSelector(msaasListingsSearchRef, selectSearchFilters);
  const sortBy = useSelector(msaasListingsSearchRef, selectSearchSortBy);
  const page = useSelector(msaasListingsSearchRef, selectSearchPagination)?.page;
  const polls = useRef(0);
  useEffect(() => {
    polls.current = 0;
  }, [pendingKey, searched, sortBy, page]);
  useEffect(() => {
    if (!enabled || !msaasListingsSearchRef || !viewing || !pendingKey) return;
    if (polls.current >= FIT_MAX_POLLS) return;
    const timer = setTimeout(() => {
      polls.current += 1;
      msaasListingsSearchRef.send({ type: 'ON_REFRESH' });
    }, FIT_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [enabled, msaasListingsSearchRef, pendingKey, loadedAt, viewing]);
};

const sortOption = (sortBy: string | undefined, side: ListingSide) =>
  (side === 'OFFERING' ? OFFERING_SORT_OPTIONS : SORT_BY_OPTIONS).find(
    (option) => option.value === sortBy
  );

export const ListingSearchPane = ({
  side,
  responsive = false,
}: {
  side: ListingSide;
  /** [MCV-1889] DD22: below 1360px show the list or one listing at a time, and filters in a drawer. */
  responsive?: boolean;
}) => {
  const isOffering = side === 'OFFERING';
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const loading = useSelector(msaasListingsSearchRef, selectSearchLoading);
  const listings = useSelector(msaasListingsSearchRef, selectSearchListings);
  const employerUser = useSelector(msaasListingsSearchRef, selectSearchEmployerUser);
  const loadedAt = useSelector(msaasListingsSearchRef, selectSearchLoadedAt);
  const sortBy = useSelector(msaasListingsSearchRef, selectSearchSortBy);
  const searchedFilters = useSelector(msaasListingsSearchRef, selectSearchFilters);
  const fit = useSearchFit(
    'b2b',
    `${employerUser?.id}:${employerUser?.employerId}:${employerUser?.userInfoId}`,
    loading || isOffering ? [] : listings.map((listing) => listing.id),
    listings
  );
  const { listings: compareListings } = useCompareListings(
    isOffering ? employerUser?.employerId : undefined
  );
  useOfferingFitPolling(isOffering);
  const offeringPrefill = useOfferingPrefill();

  // The Buying search loads when its tab is shown (and stays put while its results are fresh).
  useEffect(() => {
    if (isOffering) msaasListingsSearchRef?.send({ type: 'ON_ACTIVATE' });
  }, [isOffering, msaasListingsSearchRef, loadedAt, employerUser?.employerId]);

  // Below 1360px a row the user opens replaces the list, until Back. A new search starts at the list.
  const [detailOpen, setDetailOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    setDetailOpen(false);
    setFiltersOpen(false);
  }, [searchedFilters]);
  const openListing = useCallback(() => setDetailOpen(true), []);
  // Hidden classes only apply below 1360px; the two-pane layout above it is untouched.
  const narrowListHidden = responsive && detailOpen ? 'max-[1359px]:hidden' : '';
  const narrowDetailHidden = responsive && !detailOpen ? 'max-[1359px]:hidden' : '';

  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = sidebarWidth;
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [sidebarWidth]
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing.current) return;
    const delta = e.clientX - startX.current;
    setSidebarWidth(Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, startWidth.current + delta)));
  }, []);

  const handlePointerUp = useCallback(() => {
    isResizing.current = false;
  }, []);

  const methods = useCustomForm<SearchAndFilterValues>({
    // [MCV-1548] Search filters are validated when Search is pressed, not on
    // blur -- touching a filter and clicking off it is not a mistake. Scoped
    // here rather than in useCustomForm so the app-wide onBlur default stands
    // for the other forms; reValidateMode stays 'onChange' so a blocked search
    // clears its error as soon as the value is fixed.
    mode: 'onSubmit',
    // @ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(isOffering ? offeringSearchSchema : searchAndFilterSchema),
    defaultValues: {
      sortBy: isOffering
        ? { value: 'BEST_MATCH', label: 'Best match' }
        : { value: 'MOST_RECENT', label: 'Most Recent' },
    },
  });

  // Coming back to a tab shows the filters and sort it last searched with. The form's defaults stay
  // the empty ones, so Clear All still clears. Only the tabbed page (flag on) does this.
  const { setValue, reset } = methods;
  const restored = useRef(false);
  useEffect(() => {
    if (!responsive || restored.current || !msaasListingsSearchRef) return;
    restored.current = true;
    (Object.entries(searchedFilters ?? {}) as Array<[keyof SearchFilters, unknown]>).forEach(
      ([key, value]) => {
        if (key !== 'sortBy' && value != null) setValue(key as never, value as never);
      }
    );
    const option = sortOption(sortBy, side);
    if (option) setValue('sortBy', option);
  }, [responsive, msaasListingsSearchRef, searchedFilters, setValue, side, sortBy]);
  // Route entry (back/forward, a dashboard link) runs after this mount and drops the machine's
  // results and filters. The form follows, or it would show filters the list no longer uses.
  const lastLoadedAt = useRef(loadedAt);
  useEffect(() => {
    const dropped = lastLoadedAt.current != null && loadedAt == null;
    lastLoadedAt.current = loadedAt;
    if (responsive && dropped) reset();
  }, [responsive, loadedAt, reset]);

  const withFiltersDrawer = (filters: ReactNode) =>
    responsive ? (
      <ResponsiveFilters
        count={countAppliedFilters(searchedFilters, side)}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
      >
        {filters}
      </ResponsiveFilters>
    ) : (
      filters
    );

  const clearOfferingFilters = () => {
    reset({ sortBy: sortOption(sortBy, side) });
    msaasListingsSearchRef?.send({ type: 'ON_SEARCH', payload: { filters: {} } });
  };

  if (!msaasListingsSearchRef) return null;

  return (
    <>
      <ActionModal />
      <ListingProviderCategories>
        <FormProvider {...methods}>
          <div
            className="@container/b2b-search flex min-h-0 min-w-0 flex-1 flex-col"
            data-cy="b2b-listings-workspace"
          >
            <div
              className={classNames(
                'w-full @min-[960px]/b2b-search:sticky top-0 z-10 mb-3 bg-base px-0 @min-[960px]/b2b-search:px-6 shrink-0',
                narrowListHidden
              )}
            >
              {withFiltersDrawer(
                isOffering ? (
                  <OfferingSearchFilters
                    compareListings={compareListings}
                    prefill={offeringPrefill}
                    onSearch={() =>
                      void methods.handleSubmit((values) =>
                        msaasListingsSearchRef.send({
                          type: 'ON_SEARCH',
                          payload: { filters: values as SearchFilters },
                        })
                      )()
                    }
                    onClear={clearOfferingFilters}
                  />
                ) : (
                  <SearchAndFilter />
                )
              )}
            </div>

            <ActionRetryNotice />
            {loading ? (
              <MarketLoadingPanel label="Loading listings" className="mt-20" />
            ) : isOffering && listings.length === 0 ? (
              <div className="w-full">
                <OfferingListingsList
                  compareListings={compareListings}
                  onClearFilters={clearOfferingFilters}
                />
              </div>
            ) : (
              <div className="flex min-w-0 flex-none flex-col @min-[960px]/b2b-search:min-h-0 @min-[960px]/b2b-search:flex-1 @min-[960px]/b2b-search:flex-row">
                <div
                  style={{ '--listing-sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
                  className={classNames(
                    'max-h-[440px] w-full shrink-0 overflow-y-auto @min-[960px]/b2b-search:max-h-none @min-[960px]/b2b-search:w-[var(--listing-sidebar-width)]',
                    responsive && 'max-[1359px]:!max-h-none max-[1359px]:!w-full',
                    narrowListHidden
                  )}
                  data-cy="b2b-listings-sidebar"
                >
                  {isOffering ? (
                    <OfferingListingsList
                      compareListings={compareListings}
                      onClearFilters={clearOfferingFilters}
                      onSelectListing={openListing}
                    />
                  ) : (
                    <ListingsList fit={fit} onSelectListing={openListing} />
                  )}
                </div>

                {/* Drag handle */}
                <div
                  data-cy="b2b-listings-resize-handle"
                  className={classNames(
                    'hidden @min-[960px]/b2b-search:block w-1 shrink-0 cursor-col-resize bg-grey-900 hover:bg-primary-t-700 transition-colors select-none',
                    responsive && 'max-[1359px]:!hidden'
                  )}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                />

                <div
                  className={classNames(
                    'min-w-0 flex-none overflow-visible px-0 pt-4 @min-[960px]/b2b-search:min-h-0 @min-[960px]/b2b-search:flex-1 @min-[960px]/b2b-search:overflow-y-auto @min-[960px]/b2b-search:px-6',
                    narrowDetailHidden
                  )}
                  data-cy="b2b-listings-detail-column"
                >
                  {responsive && (
                    // #root centers text, so the button needs a left-aligned parent.
                    <div className="text-left min-[1360px]:hidden">
                      <button
                        type="button"
                        data-cy="pane-back-button"
                        className="mb-3 inline-flex min-h-10 items-center gap-1 rounded-md px-2 py-1.5 text-sm font-semibold text-primary-50 hover:bg-primary-t-900"
                        onClick={() => setDetailOpen(false)}
                      >
                        <ArrowLeftIcon className="size-4" aria-hidden="true" />
                        Back
                      </button>
                    </div>
                  )}
                  {listings.length > 0 ? (
                    <DetailPanel fit={fit} compareListings={compareListings} />
                  ) : (
                    <MarketContentPanel variant="empty">
                      <MarketEmptyPanel title="No listings matching the selected filters" />
                    </MarketContentPanel>
                  )}
                </div>
              </div>
            )}
          </div>
        </FormProvider>
      </ListingProviderCategories>
    </>
  );
};
